import { usePopup } from '@sparemin/blockhead';
import { useDispatch } from 'react-redux';

import { showError } from 'redux/modules/notification';
import { Dispatch } from 'redux/types';

/**
 * Runs `work` and opens the url it resolves to in a new tab, in a
 * popup-blocker-safe way. Resolves once the tab is pointed at the url (or
 * closed); never rejects, so callers never have to remember a `catch` to avoid
 * orphaning a tab.
 */
export type UseRunInDeferredTabResult = (
  work: () => Promise<string | undefined>,
) => Promise<void>;

const DEFAULT_LOADING_MESSAGE = 'Loading...';

/**
 * Renders a minimal placeholder into the blank tab so it doesn't look broken
 * while `work` resolves. Replaced once the tab is pointed at its real url.
 */
function writeLoadingPlaceholder(win: Window, message: string): void {
  win.document.write(
    `<!doctype html><html lang="en"><head><meta charset="utf-8" />` +
      `<title>${message}</title><style>` +
      `body{margin:0;padding:16px;font:16px -apple-system,BlinkMacSystemFont,` +
      `'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#2b2b2b;text-align:left}` +
      `</style></head><body>${message}</body></html>`,
  );

  win.document.close();
}

/**
 * Opens a new tab pointed at the result of some async work, in a
 * popup-blocker-safe way.
 *
 * Thin wrapper around Blockhead's `usePopup`: the open-blank-then-resolve
 * orchestration, the single-tab guarding and the close-on-failure all live
 * in `usePopup().openWith`. This hook only adds the bits that are specific
 * to Make — an error notification when the tab is blocked, and a loading
 * placeholder so the blank tab doesn't look broken while `work` resolves.
 *
 * The returned function MUST be called directly inside a user gesture handler
 * (e.g. an onClick/onPress), before any `await`, otherwise the browser blocks
 * the tab. While the tab is blank it shows `loadingMessage`, top-left.
 */
export default function useRunInDeferredTab(
  loadingMessage: string = DEFAULT_LOADING_MESSAGE,
): UseRunInDeferredTabResult {
  const dispatch = useDispatch<Dispatch>();

  const { openWith } = usePopup({
    target: '_blank',
    onOpen: win => writeLoadingPlaceholder(win, loadingMessage),
    onError: () =>
      dispatch(
        showError(
          'Unable to open a new tab. Allow popups for this site and try again.',
          5,
        ),
      ),
  });

  return openWith;
}
